/**
 * Per-workspace feature flags (SaaS only). Mirrors
 * `optuneutils.workspace_features` / `WorkspaceFeature` on the backend: a
 * workspace starts with Classification and "no tool calling" only, and gods
 * switch the rest on from the Admin Console. Self-hosted installs ignore the
 * flags — everything is enabled there.
 */
import { TaskType } from './taskTypes';

export const WorkspaceFeature = Object.freeze({
    entityExtraction: 'entity_extraction',
    summarization: 'summarization',
    contentGeneration: 'content_generation',
    agenticOneToolCall: 'agentic_one_tool_call',
    agenticAgentDecides: 'agentic_agent_decides',
    agenticOpenEnded: 'agentic_open_ended',
});

/** Task types that need a feature flag; Classification is always allowed. */
export const TASK_TYPE_FEATURE = Object.freeze({
    [TaskType.NER]: WorkspaceFeature.entityExtraction,
    [TaskType.summarization]: WorkspaceFeature.summarization,
    [TaskType.contentGeneration]: WorkspaceFeature.contentGeneration,
});

/** Autonomy levels that need a feature flag; level 1 ("no tool calling") is always allowed. */
export const AUTONOMY_LEVEL_FEATURE = Object.freeze({
    2: WorkspaceFeature.agenticOneToolCall,
    3: WorkspaceFeature.agenticAgentDecides,
    4: WorkspaceFeature.agenticOpenEnded,
});

/** Display groups for the Admin Console toggles, in display order. */
export const WORKSPACE_FEATURE_GROUPS = [
    {
        id: 'task-types',
        title: 'Task types',
        description: 'Classification is always available.',
        features: [
            { id: WorkspaceFeature.entityExtraction, label: 'Entity Extraction' },
            { id: WorkspaceFeature.summarization, label: 'Summarization' },
            { id: WorkspaceFeature.contentGeneration, label: 'Content Generation' },
        ],
    },
    {
        id: 'autonomy-levels',
        title: 'Autonomy levels',
        description: '"Model has no tool calling (not agentic)" is always available.',
        features: [
            { id: WorkspaceFeature.agenticOneToolCall, label: 'Agent has one predetermined tool call per intent' },
            { id: WorkspaceFeature.agenticAgentDecides, label: 'Agent decides which tools to call to' },
            { id: WorkspaceFeature.agenticOpenEnded, label: 'Agent can explore, goal can be open-ended' },
        ],
    },
];

export const WORKSPACE_FEATURE_LABELS = Object.freeze(
    Object.fromEntries(
        WORKSPACE_FEATURE_GROUPS.flatMap((group) => group.features.map((feature) => [feature.id, feature.label])),
    ),
);

const toSet = (features) => new Set(Array.isArray(features) ? features : []);

/** Whether `taskType` may be picked given the workspace's enabled features (unknown types are refused). */
export function isTaskTypeEnabled(taskType, features) {
    if (taskType === TaskType.classification) return true;
    const required = TASK_TYPE_FEATURE[taskType];
    return Boolean(required) && toSet(features).has(required);
}

/** Whether autonomy `level` may be picked given the workspace's enabled features (unparseable levels are refused). */
export function isAutonomyLevelEnabled(level, features) {
    const numeric = Number(level);
    if (numeric === 1) return true;
    const required = AUTONOMY_LEVEL_FEATURE[numeric];
    return Boolean(required) && toSet(features).has(required);
}
